import dayjs from "dayjs";
import { OrderHistoryModel } from "../repositories/history.repository";
import React from "react";
import { useOrderHistories } from "./useOrderHistory";

type HistoryStatus = "all" | "paid" | "pending" | "failed" | string;
type DateFilter =
  | { mode: "none" }
  | { mode: "month"; fromMonth?: string; toMonth?: string } // YYYY-MM
  | { mode: "date"; fromDate?: string; toDate?: string }; // YYYY-MM-DD

const inMonthRange = (iso: string, from?: string, to?: string) => {
  if (!from && !to) return true;
  const d = dayjs(iso);
  const start = from ? dayjs(from + "-01") : null;
  const end = to ? dayjs(to + "-01").endOf("month") : null;
  if (start && d.isBefore(start, "day")) return false;
  if (end && d.isAfter(end, "day")) return false;
  return true;
};

const inRange = (iso: string, filter: DateFilter) => {
  if (filter.mode === "none") return true;

  const d = dayjs(iso);

  if (filter.mode === "month") {
    const start = filter.fromMonth ? dayjs(filter.fromMonth + "-01") : null;

    const end = filter.toMonth
      ? dayjs(filter.toMonth + "-01").endOf("month")
      : null;

    if (start && d.isBefore(start, "day")) return false;
    if (end && d.isAfter(end, "day")) return false;
    return true;
  }

  if (filter.mode === "date") {
    if (filter.fromDate && d.isBefore(dayjs(filter.fromDate), "day"))
      return false;
    if (filter.toDate && d.isAfter(dayjs(filter.toDate), "day")) return false;
    return true;
  }

  return true;
};

const filterRows = (
  rows: OrderHistoryModel[],
  status: HistoryStatus,
  fromMonth?: string,
  toMonth?: string
) =>
  rows.filter((r) => {
    const st = (r.paymentStatus || "").toLowerCase();
    const byStatus = status === "all" ? true : st === status;
    const byDate = inMonthRange(r.orderDate, fromMonth, toMonth);
    return byStatus && byDate;
  });

const groupByMonth = (rows: OrderHistoryModel[]) => {
  const map = new Map<string, OrderHistoryModel[]>();

  rows.forEach((r) => {
    const key = dayjs(r.orderDate).isValid()
      ? dayjs(r.orderDate).format("YYYY-MM")
      : "unknown";

    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  });

  return map;
};

export function useOrderHistoriesView(cusId?: string) {
  const { data, isLoading, error, refetch, isFetching, invalidate } =
    useOrderHistories(cusId);

  const [status, setStatus] = React.useState<HistoryStatus>("all");
  const [fromMonth, setFromMonth] = React.useState<string>("");
  const [toMonth, setToMonth] = React.useState<string>("");

  const list = React.useMemo(
    () => (data ? filterRows(data, status, fromMonth, toMonth) : []),
    [data, status, fromMonth, toMonth]
  );

  const sortedList = React.useMemo(
    () =>
      [...list].sort(
        (a, b) => dayjs(b.orderDate).valueOf() - dayjs(a.orderDate).valueOf()
      ),
    [list]
  );

  const grouped = React.useMemo(() => groupByMonth(sortedList), [sortedList]);

  const clearFilters = () => {
    setStatus("all");
    setFromMonth("");
    setToMonth("");
  };

  return {
    data,
    list,
    grouped,
    isLoading,
    isFetching,
    error,
    refetch,
    invalidate,
    // filter state + setters
    status,
    setStatus,
    fromMonth,
    setFromMonth,
    toMonth,
    setToMonth,
    clearFilters,
  };
}
